import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Skeleton } from './Skeleton';
import AlertIndicator from './AlertIndicator';
import { toast } from 'sonner';
import {
  Activity,
  FolderKanban,
  Settings,
  Search,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  Sparkles,
  Plus,
  Folder,
  FolderOpen,
  Menu,
  PanelLeftClose,
  PanelLeft,
  Sunrise,
  AlertTriangle,
  StickyNote,
  Home,
  ChevronRight,
  MessageSquare,
  X
} from 'lucide-react';
import { cn, getInitials } from '../lib/utils';
import api from '../lib/api';
import { useChat } from '../context/ChatContext';
import { AskAnchorPanel } from './AskAnchorPanel';

export default function AppShell({ children }) {
  const { user, org, logout } = useAuth();
  const { currentTheme, isDarkMode, toggleDarkMode, currentFont, themes, setTheme } = useTheme();
  const { panelOpen, layout, closePanel: closeChatPanel } = useChat();
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Close chat panel on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && panelOpen) {
        closeChatPanel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [panelOpen, closeChatPanel]);

  // Handle new critical alerts with toast notifications
  const handleNewAlerts = useCallback((newAlerts) => {
    newAlerts.forEach((alert) => {
      const isCritical = alert.severity === 'critical';
      toast.error(
        <div className="flex items-start gap-3">
          <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${isCritical ? 'text-red-500' : 'text-orange-500'}`} />
          <div>
            <p className="font-medium">{isCritical ? 'Critical Alert' : 'High Priority Alert'}</p>
            <p className="text-sm text-muted-foreground line-clamp-2">{alert.message}</p>
          </div>
        </div>,
        {
          duration: 8000,
          action: {
            label: 'View',
            onClick: () => navigate('/alerts'),
          },
        }
      );
    });
  }, [navigate]);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const navItems = [
    { path: '/home', label: 'Home', icon: Home },
    { path: '/morning-scan', label: 'Daily Briefing', icon: Sunrise },
    { path: '/feed', label: 'Feed', icon: Activity },
    { path: '/conversations', label: 'Anchor Sessions', icon: MessageSquare },
    { path: '/projects', label: 'Projects', icon: FolderOpen },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Filter projects based on search
  const filteredProjects = searchQuery
    ? projects.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : projects;

  // Get page name for breadcrumb
  const getPageName = () => {
    const path = location.pathname;
    if (path === '/home') return 'Home';
    if (path === '/morning-scan') return 'Daily Briefing';
    if (path === '/feed') return 'Feed';
    if (path === '/conversations') return 'Anchor Sessions';
    if (path.startsWith('/projects/')) {
      const project = projects.find(p => path.includes(p.id));
      return project ? project.name : 'Project';
    }
    if (path === '/settings') return 'Settings';
    if (path === '/notes') return 'Notes';
    return 'Page';
  };

  return (
    <div className="flex h-screen bg-background" data-testid="app-shell">
      {/* Glass Morphism Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 h-screen z-40 flex flex-col',
          'backdrop-blur-2xl bg-background/80 border-r border-border/50',
          'transition-all duration-300 ease-in-out',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border/50">
          {!isCollapsed ? (
            <>
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center"
                  style={{
                    maskImage: 'radial-gradient(circle, black 50%, transparent 50%)',
                    WebkitMaskImage: 'radial-gradient(circle, black 50%, transparent 50%)'
                  }}
                >
                  <span className="text-white font-bold text-sm">A</span>
                </div>
                <span className="font-semibold text-lg">Anchor</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCollapsed(true)}
                className="h-8 w-8"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(false)}
              className="h-8 w-8 mx-auto"
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Nav Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
          {/* Navigation Items */}
          <nav className="space-y-1 mb-6" data-testid="sidebar-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  title={isCollapsed ? item.label : ''}
                >
                  <Button
                    variant={active ? 'default' : 'ghost'}
                    className={cn(
                      'w-full transition-colors',
                      active && 'bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary',
                      isCollapsed ? 'justify-center px-0' : 'justify-start'
                    )}
                  >
                    <Icon className={cn('h-4 w-4 flex-shrink-0', !isCollapsed && 'mr-2')} />
                    {!isCollapsed && item.label}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Projects Section */}
          <div className="pt-4 border-t border-border/50">
            <div className="flex items-center justify-between px-3 mb-2">
              {!isCollapsed && (
                <span className="text-xs font-semibold text-muted-foreground uppercase">Projects</span>
              )}
              {!isCollapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setShowSearch(!showSearch)}
                >
                  {showSearch ? <X className="h-3 w-3" /> : <Search className="h-3 w-3" />}
                </Button>
              )}
            </div>

            {/* Inline Search */}
            {showSearch && !isCollapsed && (
              <div className="px-3 mb-2">
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="glass-input h-8 text-sm"
                  autoFocus
                />
              </div>
            )}

            {/* Project List */}
            {loadingProjects ? (
              <div className="px-3 space-y-1">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : filteredProjects.length === 0 ? (
              !isCollapsed && (
                <div className="px-3 py-2 text-xs text-muted-foreground">
                  {searchQuery ? 'No projects found' : 'No projects yet'}
                </div>
              )
            ) : (
              <div className="space-y-1">
                {filteredProjects.slice(0, 10).map((project) => {
                  const isProjectActive = location.pathname.includes(`/projects/${project.id}`);
                  return (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      data-testid={`sidebar-project-${project.id}`}
                      title={isCollapsed ? project.name : ''}
                    >
                      <Button
                        variant="ghost"
                        className={cn(
                          'w-full transition-colors',
                          isProjectActive && 'bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary',
                          isCollapsed ? 'justify-center px-0' : 'justify-start'
                        )}
                      >
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: project.color }}
                        />
                        {!isCollapsed && <span className="ml-2 truncate">{project.name}</span>}
                      </Button>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="px-3 py-4 border-t border-border/50 space-y-1">
          <Link to="/settings" title={isCollapsed ? 'Settings' : ''}>
            <Button
              variant={isActive('/settings') ? 'default' : 'ghost'}
              className={cn(
                'w-full transition-colors',
                isActive('/settings') && 'bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary',
                isCollapsed ? 'justify-center px-0' : 'justify-start'
              )}
              data-testid="nav-settings"
            >
              <Settings className={cn('h-4 w-4 flex-shrink-0', !isCollapsed && 'mr-2')} />
              {!isCollapsed && 'Settings'}
            </Button>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={cn(
          'flex-1 flex flex-col overflow-hidden transition-all duration-300',
          isCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        {/* Glass Morphism Header */}
        <header
          className="h-16 backdrop-blur-2xl bg-background/80 border-b border-border/50 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30"
          data-testid="top-bar"
        >
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-sm">
            <Home className="h-4 w-4 text-muted-foreground" />
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{getPageName()}</span>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Alert Indicator */}
            <AlertIndicator onNewAlerts={handleNewAlerts} />

            {/* Dark Mode Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              data-testid="theme-toggle"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            {/* User Avatar with Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{getInitials(user?.name || 'U')}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline-block">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.email === 'demo@focuspane.com' && (
                  <DropdownMenuItem onClick={logout} data-testid="exit-demo-btn">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Exit Demo
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content + Panel */}
        <div className="flex-1 flex overflow-hidden">
          <main
            className={cn(
              "flex-1 overflow-auto transition-all duration-300",
              panelOpen && layout === 'fullscreen' && "hidden",
              panelOpen && layout === 'split' && "w-1/2 flex-none"
            )}
            data-testid="main-content"
          >
            {children}
          </main>

          {/* Global AskAnchorPanel */}
          <AskAnchorPanel />
        </div>
      </div>
    </div>
  );
}
